import PageHeading from "../components/PageHeading";
import SearchPending from "../components/SearchPending";
import { useDebouncedValue } from "../hooks/useDebouncedValue";
import { createListSearch } from "../utils/listSearch";
import { apiErrorMessage } from "../utils/apiError";
import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { BookOpen, Plus, Search, Download, FileText, Eye, Trash2 } from "lucide-react";
import { message } from "antd";
import { formatJsDate } from "../utils/formatageDate";
import axiosInstance from "../axios/AxiosInstance";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Label } from "../ui/Label";
import { Input } from "../ui/Input";
import { Button } from "../ui/Button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/Select";

const categories = [
  "Toutes",
  "Admin/PLD",
  "Commercial",
  "Formation",
  "Juridique",
  "Livret",
  "Paie",
  "Ressources Humaines",
  "RSE",
  "Autre",
];

const BibliothequePage = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [appliedSearch, searching] = useDebouncedValue(searchTerm);
  const [categorieSelectionnee, setCategorieSelectionnee] = useState("Toutes");
  const [newDocCategory, setNewDocCategory] = useState("");
      const [isAddDocumentOpen, setIsAddDocumentOpen] = useState(false);
  const [documentName, setDocumentName] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const inputFileRef = useRef(null);
  const [documents, setDocuments] = useState([]);
          const [isLoadingDocuments, setIsLoadingDocuments] = useState(false);
  // Filtrage des documents
  const getFilteredDocuments = () => {
    let filtered = documents;

    if (categorieSelectionnee !== "Toutes") {
      filtered = filtered.filter(doc => doc.category === categorieSelectionnee);
    }

    filtered = filtered.filter(createListSearch(appliedSearch, ["name", "category", "description"]));

    return filtered;
  };

  const filteredDocs = getFilteredDocuments();
  const [isAddingDocument, setIsAddingDocument] = useState(false);

  const base64toBlob = (base64String, contentType = "application/pdf") => {
    const byteCharacters = window.atob(base64String);
    const byteNumbers = new Array(byteCharacters.length);

    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }

    const byteArray = new Uint8Array(byteNumbers);
    return new Blob([byteArray], { type: contentType });
  };

  
  const getDocument = (documentId) => {
    axiosInstance
      .get(`/global-doc/${documentId}`)
      .then((res) => {
        const pdfBlob = base64toBlob(res.data.data);
        const pdfUrl = URL.createObjectURL(pdfBlob);
        window.open(pdfUrl, "_blank");
      })
      .catch((error) => message.error(apiErrorMessage(error, "Une erreur s'est produite")));
  };
  const deleteDocument = (documentId, idx) => {
    axiosInstance
      .delete(`/global-doc/freelance/${documentId}`)
      .then((res) => {
        const newDocuments = [...documents];
        newDocuments.splice(idx, 1);
        setDocuments(newDocuments);
        message.success("Document supprimé !");
      })
      .catch((error) => message.error(apiErrorMessage(error, "Une erreur s'est produite")));
  };

  
  const downloadDocument = (doc) => {
    axiosInstance
      .get(`/global-doc/${doc._id}`)
      .then((res) => {
        const pdfBlob = base64toBlob(res.data.data);
        const url = URL.createObjectURL(pdfBlob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `${doc.name || "document"}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      })
      .catch((error) => message.error(apiErrorMessage(error, "Une erreur s'est produite")));
  };

  const addDocument = () => {
    if (!documentName || !newDocCategory || !selectedFile) {
      message.error("Veuillez remplir tous les champs !");
      return;
    }

    setIsAddingDocument(true);
    const formData = new FormData();
    formData.append("file", selectedFile);
    formData.append("name", documentName);
    formData.append("category", newDocCategory);

    axiosInstance
      .post("/global-doc", formData)
      .then((res) => {
        message.success("Document ajouté !");
        const newDocuments = [...documents, res.data];
        setDocuments(newDocuments);
        setIsAddDocumentOpen(false);
        setDocumentName("");
        setNewDocCategory("");
        setSelectedFile(null);
      })
      .catch((error) => {
        setSelectedFile(null);
        message.error(apiErrorMessage(error, "Impossible d’ajouter le document."));
      })
      .finally(() => {
        setIsAddingDocument(false);
      });
  };

  useEffect(() => {
    const controller = new AbortController();
    setIsLoadingDocuments(true);
    axiosInstance
      .get("global-doc/freelance", { signal: controller.signal })
      .then((res) => {
        setDocuments(res.data);
      })
      .catch((error) => {
        const detail = apiErrorMessage(error, "Impossible de charger la bibliothèque.");
        if (detail) message.error(detail);
      })
      .finally(() => {
        if (!controller.signal.aborted) setIsLoadingDocuments(false);
      });
    return () => controller.abort();
  }, []);

  return (
    <div className="bg-slate-50">
      <div className="">
        {/* Header */}
        <motion.div
          className="rounded-2xl mb-6"
        >
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center space-x-4">
              <div><PageHeading icon="library" title="Bibliothèque" description={<> Gérez et organisez tous vos documents importants </>} />
              </div>
            </div>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setIsAddDocumentOpen(true)}
              className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl font-medium transition-colors duration-200 shadow-lg"
            >
              <Plus className="w-5 h-5" />
              <span>Nouveau document</span>
            </motion.button>
          </div>
        </motion.div>

        {/* Search and Filters */}
        <motion.div
          className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 mb-8"
        >
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            {/* Search */}
            <div className="flex-1 max-w-md">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Rechercher un document..."
                  className="pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 w-full"
                />
              </div>
              <SearchPending pending={searching} />
            </div>

            {/* Category Filter */}
            <div className="flex items-center space-x-4">
              <select
                value={categorieSelectionnee}
                onChange={(e) => setCategorieSelectionnee(e.target.value)}
                className="px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
              >
                {categories.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
              </select>


            </div>
          </div>
        </motion.div>

        {/* Tableau des documents */}
        <motion.div
        >
          {isLoadingDocuments ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            </div>
          ) : filteredDocs.length === 0 ? (
            <div className="text-center py-12">
              <BookOpen className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-900 mb-2">Aucun document trouvé</h3>
              <p className="text-slate-500">
                {searchTerm ? "Essayez de modifier vos critères de recherche" : "Commencez par ajouter votre premier document"}
              </p>
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
              <div className="app-table-container">
                <table className="app-table" aria-label="Documents de la bibliothèque">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                        Document
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                        Catégorie
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                        Date
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                        Type
                      </th>
                      <th className="px-6 py-4 text-center text-xs font-medium text-slate-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-slate-200">
                    <AnimatePresence>
                      {filteredDocs.map((doc, index) => (
                        <motion.tr
                          key={doc._id}
                          className="hover:bg-slate-50 transition-colors duration-200"
                        >
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="bg-slate-100 p-2 rounded-lg mr-3">
                                <FileText className="w-4 h-4 text-slate-600" />
                              </div>
                              <div>
                                <div className="text-sm font-medium text-slate-900">
                                  {doc.name}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              {doc.category || "Sans catégorie"}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-slate-900">
                              {doc.createdAt ? formatJsDate(doc.createdAt) : ""}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-slate-900 uppercase">
                              {doc.type || "pdf"}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            <div className="flex items-center justify-center space-x-2">
                              <motion.button
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.9 }}
                                onClick={() => getDocument(doc._id)}
                                className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all duration-200"
                                title="Voir" aria-label={`Voir ${doc.name}`}
                              >
                                <Eye className="w-4 h-4" />
                              </motion.button>
                              <motion.button
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.9 }}
                                onClick={() => downloadDocument(doc)}
                                className="p-2 text-slate-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-all duration-200"
                                title="Télécharger" aria-label={`Télécharger ${doc.name}`}
                              >
                                <Download className="w-4 h-4" />
                              </motion.button>
                              <motion.button
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.9 }}
                                onClick={() => deleteDocument(doc._id, documents.indexOf(doc))}
                                className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all duration-200"
                                title="Supprimer" aria-label={`Supprimer ${doc.name}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </motion.button>
                            </div>
                          </td>
                        </motion.tr>
                      ))}
                    </AnimatePresence>
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </motion.div>
      </div>

      {/* Hidden file input */}
      <input
        type="file"
        ref={inputFileRef}
        onChange={(e) => setSelectedFile(e.target.files[0])}
        style={{ display: "none" }}
        accept=".pdf,.doc,.docx,.txt"
      />

      {/* Add Document Modal */}
      {isAddDocumentOpen && (
        <Dialog
          open={isAddDocumentOpen}
          onOpenChange={setIsAddDocumentOpen}
          className="bg-white"
        >
          <DialogContent className="bg-white">
            <DialogHeader>
              <DialogTitle>Ajouter un nouveau document</DialogTitle>
            </DialogHeader>

            <div className="grid gap-4 py-4 bg-white">
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="titre" className="text-right font-bold">
                  Titre
                </Label>
                <Input
                  placeholder="Titre du document"
                  value={documentName}
                  onChange={(e) => setDocumentName(e.target.value)}
                  className="col-span-3"
                />
              </div>

              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="categorie" className="text-right font-bold">
                  Catégorie
                </Label>
                <Select
                  value={newDocCategory}
                  onValueChange={(value) => setNewDocCategory(value)}
                >
                  <SelectTrigger className="col-span-3">
                    <SelectValue placeholder="Sélectionner une catégorie" />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    {categories
                      .filter((cat) => cat !== "Toutes")
                      .map((cat) => (
                        <SelectItem key={cat} value={cat}>
                          {cat}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-4 items-center gap-4 text-black">
                <Label htmlFor="fichier" className="text-right font-bold">
                  Fichier
                </Label>
                <Input
                  id="fichier"
                  type="file"
                  onChange={(e) => setSelectedFile(e.target.files[0])}
                  className="col-span-3 bg-gray-200 text-black"
                  ref={inputFileRef}
                />
              </div>
            </div>

            <div className="flex justify-end gap-4 mt-4">
              <Button
                variant="outline"
                onClick={() => setIsAddDocumentOpen(false)}
              >
                Annuler
              </Button>
              <Button onClick={addDocument} disabled={isAddingDocument}>
                {isAddingDocument ? "Ajout en cours..." : "Ajouter"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default BibliothequePage;
